/**
 * Modal para crear/editar Concepto Financiero
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { X, Loader2 } from "lucide-react";
import {
  useCreateFinancialConcept,
  useUpdateFinancialConcept,
} from "@/hooks/useCatalogs";
import type { FinancialConcept } from "@/lib/services/catalogs";

const financialConceptSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  code: z.string().min(2, "El código debe tener al menos 2 caracteres"),
  type: z.enum(["income", "expense"]),
  category: z.enum(["membership", "event", "monthly", "other"]),
  defaultAmount: z.coerce.number().optional(),
  isRecurring: z.boolean(),
  description: z.string().optional(),
});

type FinancialConceptForm = z.infer<typeof financialConceptSchema>;

interface FinancialConceptModalProps {
  isOpen: boolean;
  onClose: () => void;
  concept?: FinancialConcept | null;
}

const typeOptions = [
  { value: "income", label: "Ingreso" },
  { value: "expense", label: "Gasto" },
];

const categoryOptions = [
  { value: "membership", label: "Membresía" },
  { value: "event", label: "Evento" },
  { value: "monthly", label: "Mensual" },
  { value: "other", label: "Otro" },
];

export default function FinancialConceptModal({
  isOpen,
  onClose,
  concept,
}: FinancialConceptModalProps) {
  const isEditing = !!concept;

  const createMutation = useCreateFinancialConcept();
  const updateMutation = useUpdateFinancialConcept();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FinancialConceptForm>({
    resolver: zodResolver(financialConceptSchema),
    defaultValues: {
      name: concept?.name || "",
      code: concept?.code || "",
      type: concept?.type || "income",
      category: concept?.category || "other",
      defaultAmount: concept?.defaultAmount,
      isRecurring: concept?.isRecurring || false,
      description: concept?.description || "",
    },
  });

  // Reset form when concept changes
  useEffect(() => {
    if (isOpen) {
      reset({
        name: concept?.name || "",
        code: concept?.code || "",
        type: concept?.type || "income",
        category: concept?.category || "other",
        defaultAmount: concept?.defaultAmount,
        isRecurring: concept?.isRecurring || false,
        description: concept?.description || "",
      });
    }
  }, [concept, isOpen, reset]);

  const onSubmit = async (data: FinancialConceptForm) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: concept.id,
          data: data as Partial<FinancialConcept>,
        });
      } else {
        await createMutation.mutateAsync({
          ...data,
          status: "active",
        });
      }
      onClose();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Concepto" : "Nuevo Concepto"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Nombre *"
            {...register("name")}
            error={errors.name?.message}
            placeholder="Ej: Cuota Mensual"
          />

          <Input
            label="Código *"
            {...register("code")}
            error={errors.code?.message}
            placeholder="Ej: MENSUAL"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo *
              </label>
              <select
                {...register("type")}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoría *
              </label>
              <select
                {...register("category")}
                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Input
            label="Monto por defecto"
            type="number"
            {...register("defaultAmount")}
            error={errors.defaultAmount?.message}
            placeholder="0"
          />

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register("isRecurring")}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              Es recurrente (se genera cada periodo)
            </span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              {...register("description")}
              rows={3}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              placeholder="Descripción opcional..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
